import { forwardRef, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-charcoal-600 mb-2"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-400">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={clsx(
              'w-full px-4 py-3 bg-white border rounded-xl',
              'text-charcoal-600 placeholder:text-charcoal-300',
              'focus:outline-none focus:ring-2 focus:border-primary-400',
              'transition-all duration-200 min-h-touch',
              error
                ? 'border-rose-300 focus:ring-rose-200'
                : 'border-cream-400 focus:ring-primary-300',
              leftIcon && 'pl-12',
              rightIcon && 'pr-12',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-charcoal-400">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-2 text-sm text-rose-400">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-2 text-sm text-charcoal-400">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// Textarea component
interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const textareaId = id || label?.toLowerCase().replace(/\s/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-charcoal-600 mb-2"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={clsx(
            'w-full px-4 py-3 bg-white border rounded-xl',
            'text-charcoal-600 placeholder:text-charcoal-300',
            'focus:outline-none focus:ring-2 focus:border-primary-400',
            'transition-all duration-200 min-h-[120px] resize-y',
            error
              ? 'border-rose-300 focus:ring-rose-200'
              : 'border-cream-400 focus:ring-primary-300',
            className
          )}
          {...props}
        />
        {error && (
          <p className="mt-2 text-sm text-rose-400">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-2 text-sm text-charcoal-400">{hint}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

// Select component
interface SelectProps extends InputHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: { value: string; label: string }[];
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, className, id, ...props }, ref) => {
    const selectId = id || label?.toLowerCase().replace(/\s/g, '-');

    return (
      <div className={clsx('w-full', className)}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-charcoal-600 mb-2"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={clsx(
              'w-full px-4 py-3 pr-10 bg-white border rounded-xl',
              'text-charcoal-600',
              'focus:outline-none focus:ring-2 focus:border-primary-400',
              'transition-all duration-200 min-h-touch',
              'appearance-none cursor-pointer',
              error
                ? 'border-rose-300 focus:ring-rose-200'
                : 'border-cream-400 focus:ring-primary-300'
            )}
            {...props}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-400 pointer-events-none" />
        </div>
        {error && (
          <p className="mt-2 text-sm text-rose-400">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-2 text-sm text-charcoal-400">{hint}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Input;
